import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import type {
  AppUser, Cliente, Unidade, Contrato, Obra, CentroCusto,
  Fornecedor, CategoriaOrcamentaria, CargoFuncao, FaixaCusto,
  Colaborador, ItemOrcado, Requisicao, Cotacao, OrdemCompra,
  ContaPagar, Pagamento, Aprovacao, LogAuditoria,
  ItemRequisicao, ItemCotacao, ItemOrdemCompra, AlocacaoColaborador
} from '../types'
import { nowISO } from '../utils'
import { lumi } from '../lib/lumi'

interface AppState {
  currentUser: AppUser | null
  users: AppUser[]
  clientes: Cliente[]
  unidades: Unidade[]
  contratos: Contrato[]
  obras: Obra[]
  centrosCusto: CentroCusto[]
  fornecedores: Fornecedor[]
  categorias: CategoriaOrcamentaria[]
  cargos: CargoFuncao[]
  faixasCusto: FaixaCusto[]
  colaboradores: Colaborador[]
  itensOrcados: ItemOrcado[]
  requisicoes: Requisicao[]
  itensRequisicao: ItemRequisicao[]
  cotacoes: Cotacao[]
  itensCotacao: ItemCotacao[]
  ordensCompra: OrdemCompra[]
  itensOrdemCompra: ItemOrdemCompra[]
  contasPagar: ContaPagar[]
  pagamentos: Pagamento[]
  aprovacoes: Aprovacao[]
  alocacoesColaboradores: AlocacaoColaborador[]
  logs: LogAuditoria[]
}

interface AppContextType extends AppState {
  usersLoading: boolean
  usersPersistence: 'lumi' | 'memory'
  authSessionRestoring: boolean
  login: (email: string, senha: string) => boolean
  logout: () => void
  saveUser: (user: Omit<AppUser, 'id'> & { id?: string; senha?: string }, userId?: string) => Promise<AppUser>
  setUserStatus: (userId: string, status: AppUser['status']) => Promise<void>
  deleteUser: (userId: string) => Promise<void>
  changeOwnPassword: (senhaAtual: string, novaSenha: string) => Promise<void>
  setClientes: React.Dispatch<React.SetStateAction<Cliente[]>>
  setUnidades: React.Dispatch<React.SetStateAction<Unidade[]>>
  setContratos: React.Dispatch<React.SetStateAction<Contrato[]>>
  setObras: React.Dispatch<React.SetStateAction<Obra[]>>
  setCentrosCusto: React.Dispatch<React.SetStateAction<CentroCusto[]>>
  setFornecedores: React.Dispatch<React.SetStateAction<Fornecedor[]>>
  setCategorias: React.Dispatch<React.SetStateAction<CategoriaOrcamentaria[]>>
  setCargos: React.Dispatch<React.SetStateAction<CargoFuncao[]>>
  setFaixasCusto: React.Dispatch<React.SetStateAction<FaixaCusto[]>>
  setColaboradores: React.Dispatch<React.SetStateAction<Colaborador[]>>
  setItensOrcados: React.Dispatch<React.SetStateAction<ItemOrcado[]>>
  setRequisicoes: React.Dispatch<React.SetStateAction<Requisicao[]>>
  setItensRequisicao: React.Dispatch<React.SetStateAction<ItemRequisicao[]>>
  setCotacoes: React.Dispatch<React.SetStateAction<Cotacao[]>>
  setItensCotacao: React.Dispatch<React.SetStateAction<ItemCotacao[]>>
  setOrdensCompra: React.Dispatch<React.SetStateAction<OrdemCompra[]>>
  setItensOrdemCompra: React.Dispatch<React.SetStateAction<ItemOrdemCompra[]>>
  setContasPagar: React.Dispatch<React.SetStateAction<ContaPagar[]>>
  setPagamentos: React.Dispatch<React.SetStateAction<Pagamento[]>>
  setAprovacoes: React.Dispatch<React.SetStateAction<Aprovacao[]>>
  setAlocacoesColaboradores: React.Dispatch<React.SetStateAction<AlocacaoColaborador[]>>
  addLog: (entidade_tipo: string, entidade_id: string, acao: LogAuditoria['acao'], detalhes: string) => void
}

const AppContext = createContext<AppContextType | null>(null)

const APP_CREATOR_ID = 'app-4s'
const APP_CREATOR_EMAIL = 'sistema@app4s.local'
const APP_AUTH_SESSION_KEY = 'app4s.auth.session.v1'
let auditLogFallbackCounter = 0

function createAuditLogId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `l${crypto.randomUUID()}`
  }

  auditLogFallbackCounter += 1
  return `l${Date.now()}-${auditLogFallbackCounter}-${Math.random().toString(36).slice(2, 10)}`
}

type StoredAuthSession = {
  userId: string
  email: string
}

function notifyApp(message: string, type: 'success' | 'error' = 'error') {
  window.dispatchEvent(new CustomEvent('app-notification', { detail: { message, type } }))
}

const SEED_USERS: AppUser[] = [
  { id: 'u-admin-hello', nome: 'Hello', email: 'hello@ruatrez.com', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
  { id: 'u-admin-teste-lumi', nome: 'Usuario Teste', email: 'teste@4sengenharia.com.br', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
  { id: 'u-admin-rafael', nome: 'Rafael', email: 'rafael@4sengenharia.com', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
  { id: 'u-admin-financeiro', nome: 'Financeiro', email: 'financeiro@4sengenharia.com', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
  { id: 'u-admin-principal', nome: 'Administrador', email: 'admin@4sengenharia.com.br', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
  { id: 'u-admin-carolina', nome: 'Carolina Alves', email: 'carolina.alves@4sengenharia.com', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
  { id: 'u-admin-fernanda', nome: 'Fernanda', email: 'fernanda@ruatrez.com', perfil: 'ADMINISTRADOR', status: 'ATIVO' },
]

function normalizeEmail(email: string) {
  return email.trim().toLowerCase()
}

function readStoredAuthSession(): StoredAuthSession | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(APP_AUTH_SESSION_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredAuthSession>
    const userId = String(parsed.userId ?? '').trim()
    const email = normalizeEmail(String(parsed.email ?? ''))

    if (!userId || !email) return null
    return { userId, email }
  } catch {
    return null
  }
}

function writeStoredAuthSession(user: AppUser) {
  if (typeof window === 'undefined') return

  window.localStorage.setItem(APP_AUTH_SESSION_KEY, JSON.stringify({
    userId: user.id,
    email: normalizeEmail(user.email),
  }))
}

function clearStoredAuthSession() {
  if (typeof window === 'undefined') return

  window.localStorage.removeItem(APP_AUTH_SESSION_KEY)
}

function hashPassword(password: string) {
  let hash = 5381

  for (const character of password) {
    hash = (hash * 33) ^ character.charCodeAt(0)
  }

  return `4s_${(hash >>> 0).toString(16)}`
}

function validatePassword(password: string) {
  if (password.trim().length < 6) {
    return 'A senha precisa ter pelo menos 6 caracteres.'
  }

  return null
}

function checkPassword(user: AppUser, password: string) {
  const storedHash = user.senha_hash
  if (!storedHash) return password === 'admin123'
  return storedHash === hashPassword(password)
}

const SEED_CLIENTES: Cliente[] = []

const SEED_UNIDADES: Unidade[] = []

const SEED_FORNECEDORES: Fornecedor[] = []

const SEED_CATEGORIAS: CategoriaOrcamentaria[] = []

const SEED_CONTRATOS: Contrato[] = []

const SEED_OBRAS: Obra[] = []

const SEED_CENTROS: CentroCusto[] = []

const SEED_ITENS: ItemOrcado[] = []

const SEED_REQUISICOES: Requisicao[] = []

const SEED_COTACOES: Cotacao[] = []

const SEED_ITENS_REQUISICAO: ItemRequisicao[] = []

const SEED_ITENS_COTACAO: ItemCotacao[] = []

const SEED_ORDENS: OrdemCompra[] = []

const SEED_CONTAS: ContaPagar[] = []

const SEED_ITENS_OC: ItemOrdemCompra[] = []

const SEED_PAGAMENTOS: Pagamento[] = []

const SEED_ALOCACOES: AlocacaoColaborador[] = []

const SEED_APROVACOES: Aprovacao[] = []

const SEED_LOGS: LogAuditoria[] = []

type RawUserRecord = Partial<AppUser> & {
  _id?: string
  createdAt?: string
  updatedAt?: string
  deletedAt?: string
}

const usuariosEntity = lumi.entities.usuarios as unknown as {
  list: (options?: { sort?: Record<string, 1 | -1>; limit?: number; skip?: number }) => Promise<{ list: RawUserRecord[] }>
  create: (data: Record<string, unknown>) => Promise<RawUserRecord>
  update: (id: string, data: Record<string, unknown>) => Promise<RawUserRecord>
  delete: (id: string) => Promise<void>
}

type PersistableRecord = {
  _id?: string
  id: string
  creator?: string
  creatorEmail?: string
  createdAt?: string
  updatedAt?: string
  deletedAt?: string
}

type EntityClient<T extends PersistableRecord> = {
  list: (options?: { sort?: Record<string, 1 | -1> }) => Promise<{ list: T[] }>
  create: (data: Record<string, unknown>) => Promise<T>
}

const seedEntityPromises = new Map<string, Promise<PersistableRecord[]>>()

function getEntityClient<T extends PersistableRecord>(entityName: string): EntityClient<T> {
  return (lumi.entities as unknown as Record<string, EntityClient<T>>)[entityName]
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function withLumiRetry<T>(operation: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      lastError = error
      if (attempt < attempts - 1) {
        await sleep(350 * (attempt + 1))
      }
    }
  }

  throw lastError
}

function getEntityVersionKey(record: PersistableRecord) {
  return String(record.id ?? record._id ?? '')
}

function getEntityVersionTime(record: PersistableRecord) {
  return Date.parse(String(record.updatedAt ?? record.createdAt ?? '')) || 0
}

function isNewerVersion(candidate: PersistableRecord, current?: PersistableRecord) {
  if (!current) return true

  const candidateTime = getEntityVersionTime(candidate)
  const currentTime = getEntityVersionTime(current)
  if (candidateTime !== currentTime) return candidateTime > currentTime

  return String(candidate._id ?? '') > String(current._id ?? '')
}

function normalizeEntityRecord<T extends PersistableRecord>(record: T): T {
  return {
    ...record,
    id: String(record.id ?? record._id ?? `rec-${Date.now()}`),
  }
}

function compactEntityVersions<T extends PersistableRecord>(records: T[]): T[] {
  const latestById = new Map<string, T>()

  records.forEach(record => {
    const normalized = normalizeEntityRecord(record)
    const key = getEntityVersionKey(normalized)
    if (!key) return

    if (isNewerVersion(normalized, latestById.get(key))) {
      latestById.set(key, normalized)
    }
  })

  return Array.from(latestById.values())
    .filter(record => !record.deletedAt)
    .sort((a, b) => getEntityVersionTime(b) - getEntityVersionTime(a))
}

function removeUndefinedFields<T extends object>(payload: T): T {
  return Object.fromEntries(
    Object.entries(payload as Record<string, unknown>).filter(([, value]) => value !== undefined),
  ) as T
}

function stripInternalFields(record: PersistableRecord): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...record }
  delete payload._id
  return removeUndefinedFields(payload)
}

function withAppAuditFields(record: PersistableRecord) {
  return removeUndefinedFields({
    ...stripInternalFields(record),
    creator: record.creator ?? APP_CREATOR_ID,
    creatorEmail: record.creatorEmail ?? APP_CREATOR_EMAIL,
  })
}

function getComparablePayload(record: PersistableRecord) {
  const payload = stripInternalFields(record)
  delete payload.updatedAt
  return JSON.stringify(payload)
}

async function syncEntityChanges<T extends PersistableRecord>(
  entityName: string,
  previous: T[],
  next: T[],
  applySavedList: React.Dispatch<React.SetStateAction<T[]>>,
): Promise<T[]> {
  const client = getEntityClient<T>(entityName)
  const previousById = new Map(previous.map(item => [item.id, item]))
  const nextById = new Map(next.map(item => [item.id, item]))
  const createdOrUpdatedAt = nowISO()

  const deletedRecords = previous.filter(item => !nextById.has(item.id))
  await Promise.all(
    deletedRecords.map(item =>
      withLumiRetry(() => client.create(removeUndefinedFields({
        ...withAppAuditFields(item),
        createdAt: item.createdAt ?? createdOrUpdatedAt,
        updatedAt: createdOrUpdatedAt,
        deletedAt: createdOrUpdatedAt,
      }))),
    ),
  )

  const savedRecords = await Promise.all(
    next.map(async item => {
      const previousItem = previousById.get(item.id)
      if (previousItem && getComparablePayload(previousItem) === getComparablePayload(item)) {
        return previousItem
      }

      const payload = removeUndefinedFields({
        ...withAppAuditFields(item),
        createdAt: item.createdAt ?? previousItem?.createdAt ?? createdOrUpdatedAt,
        updatedAt: createdOrUpdatedAt,
        deletedAt: undefined,
      })

      return withLumiRetry(() => client.create(payload))
    }),
  )

  const savedByLocalId = new Map(
    savedRecords.map((record, index) => [
      next[index].id,
      normalizeEntityRecord({
        ...record,
        id: next[index].id,
      }),
    ]),
  )

  applySavedList(current =>
    current.map(item => savedByLocalId.get(item.id) ?? item),
  )

  return next.map(item => savedByLocalId.get(item.id) ?? item)
}

async function createSeedRecords<T extends PersistableRecord>(
  entityName: string,
  seed: T[],
): Promise<T[]> {
  if (seed.length === 0) return []

  const existingPromise = seedEntityPromises.get(entityName) as Promise<T[]> | undefined
  if (existingPromise) return existingPromise

  const client = getEntityClient<T>(entityName)
  const createdOrUpdatedAt = nowISO()
  const seedPromise = Promise.all(
    seed.map(item =>
      withLumiRetry(() => client.create(removeUndefinedFields({
        ...withAppAuditFields(item),
        createdAt: item.createdAt ?? createdOrUpdatedAt,
        updatedAt: createdOrUpdatedAt,
      }))),
    ),
  )

  seedEntityPromises.set(entityName, seedPromise as Promise<PersistableRecord[]>)
  return seedPromise
}

function useLumiEntityState<T extends PersistableRecord>(
  entityName: string,
  initialValue: T[],
) {
  const [items, setItems] = useState<T[]>(initialValue)
  const itemsRef = useRef<T[]>(initialValue)
  const syncPromiseRef = useRef<Promise<T[]>>(Promise.resolve(initialValue))

  const replaceItems = useCallback((nextItems: T[]) => {
    const normalized = nextItems.map(normalizeEntityRecord)
    itemsRef.current = normalized
    setItems(normalized)
    syncPromiseRef.current = Promise.resolve(normalized)
  }, [])

  const persistentSetItems: React.Dispatch<React.SetStateAction<T[]>> = useCallback(action => {
    const previous = itemsRef.current
    const next = typeof action === 'function'
      ? (action as (prevState: T[]) => T[])(previous)
      : action

    itemsRef.current = next
    setItems(next)

    syncPromiseRef.current = syncPromiseRef.current
      .catch(() => previous)
      .then(savedPrevious => syncEntityChanges(entityName, savedPrevious, next, setItems))
      .catch(error => {
        console.error(`Falha ao sincronizar ${entityName} no Lumi:`, error)
        itemsRef.current = previous
        setItems(previous)
        notifyApp('Erro, tente novamente mais tarde.', 'error')
        return previous
      })
  }, [entityName])

  return [items, persistentSetItems, replaceItems] as const
}

function normalizeUser(record: RawUserRecord): AppUser {
  return {
    _id: record._id,
    id: String(record.id ?? record._id ?? `u${Date.now()}`),
    nome: String(record.nome ?? ''),
    email: String(record.email ?? ''),
    perfil: record.perfil ?? 'OPERACIONAL',
    status: record.status ?? 'ATIVO',
    senha_hash: record.senha_hash,
    senha_alterada_em: record.senha_alterada_em,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    deletedAt: record.deletedAt,
  }
}

function userPayload(user: AppUser) {
  return removeUndefinedFields({
    id: user.id,
    nome: user.nome,
    email: user.email.trim().toLowerCase(),
    perfil: user.perfil,
    status: user.status,
    senha_hash: user.senha_hash,
    senha_alterada_em: user.senha_alterada_em,
    deletedAt: user.deletedAt,
    creator: 'app-4s',
    creatorEmail: 'sistema@app4s.local',
  })
}

async function readUsersFromLumi() {
  const { list } = await withLumiRetry(() => usuariosEntity.list({ sort: { updatedAt: -1 }, limit: 1000 }))
  const latestById = new Map<string, RawUserRecord>()

  list.forEach(record => {
    const normalized = normalizeUser(record)
    const key = normalized.id
    const current = latestById.get(key)

    if (isNewerVersion(normalized as unknown as PersistableRecord, current as unknown as PersistableRecord | undefined)) {
      latestById.set(key, record)
    }
  })

  return Array.from(latestById.values())
    .map(normalizeUser)
    .filter(user => !user.deletedAt)
}

async function syncRequiredUsersInLumi(existingUsers: AppUser[]) {
  const now = nowISO()
  const usersByEmail = new Map(
    existingUsers.map(user => [normalizeEmail(user.email), user]),
  )

  await Promise.all(
    SEED_USERS.filter(seedUser => !usersByEmail.has(normalizeEmail(seedUser.email))).map(seedUser => {
      const existing = usersByEmail.get(normalizeEmail(seedUser.email))

      if (existing) return Promise.resolve(existing)

      return withLumiRetry(() => usuariosEntity.create({
        ...userPayload({
          ...seedUser,
          senha_hash: hashPassword('admin123'),
          senha_alterada_em: now,
        }),
        createdAt: now,
        updatedAt: now,
      }))
    }),
  )
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [appNotification, setAppNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [authSession, setAuthSession] = useState(() => {
    const session = readStoredAuthSession()
    return { session, checked: !session }
  })
  const initialAuthSessionRef = useRef(authSession.session)
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null)
  const [users, setUsers] = useState<AppUser[]>([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [usersPersistence, setUsersPersistence] = useState<'lumi' | 'memory'>('memory')
  const [clientes, setClientes, replaceClientes] = useLumiEntityState<Cliente>('clientes', SEED_CLIENTES)
  const [unidades, setUnidades, replaceUnidades] = useLumiEntityState<Unidade>('unidades', SEED_UNIDADES)
  const [contratos, setContratos, replaceContratos] = useLumiEntityState<Contrato>('contratos', SEED_CONTRATOS)
  const [obras, setObras, replaceObras] = useLumiEntityState<Obra>('obras', SEED_OBRAS)
  const [centrosCusto, setCentrosCusto, replaceCentrosCusto] = useLumiEntityState<CentroCusto>('centros_de_custo', SEED_CENTROS)
  const [fornecedores, setFornecedores, replaceFornecedores] = useLumiEntityState<Fornecedor>('fornecedores', SEED_FORNECEDORES)
  const [categorias, setCategorias, replaceCategorias] = useLumiEntityState<CategoriaOrcamentaria>('categorias_orcamentarias', SEED_CATEGORIAS)
  const [cargos, setCargos, replaceCargos] = useLumiEntityState<CargoFuncao>('cargos_funcoes', [])
  const [faixasCusto, setFaixasCusto, replaceFaixasCusto] = useLumiEntityState<FaixaCusto>('faixas_custo', [])
  const [colaboradores, setColaboradores, replaceColaboradores] = useLumiEntityState<Colaborador>('colaboradores', [])
  const [itensOrcados, setItensOrcados, replaceItensOrcados] = useLumiEntityState<ItemOrcado>('itens_orcados', SEED_ITENS)
  const [requisicoes, setRequisicoes, replaceRequisicoes] = useLumiEntityState<Requisicao>('requisicoes', SEED_REQUISICOES)
  const [itensRequisicao, setItensRequisicao, replaceItensRequisicao] = useLumiEntityState<ItemRequisicao>('itens_requisicao', SEED_ITENS_REQUISICAO)
  const [cotacoes, setCotacoes, replaceCotacoes] = useLumiEntityState<Cotacao>('cotacoes', SEED_COTACOES)
  const [itensCotacao, setItensCotacao, replaceItensCotacao] = useLumiEntityState<ItemCotacao>('itens_cotacao', SEED_ITENS_COTACAO)
  const [ordensCompra, setOrdensCompra, replaceOrdensCompra] = useLumiEntityState<OrdemCompra>('ordens_compra', SEED_ORDENS)
  const [itensOrdemCompra, setItensOrdemCompra, replaceItensOrdemCompra] = useLumiEntityState<ItemOrdemCompra>('itens_ordem_compra', SEED_ITENS_OC)
  const [contasPagar, setContasPagar, replaceContasPagar] = useLumiEntityState<ContaPagar>('contas_pagar', SEED_CONTAS)
  const [pagamentos, setPagamentos, replacePagamentos] = useLumiEntityState<Pagamento>('pagamentos', SEED_PAGAMENTOS)
  const [aprovacoes, setAprovacoes, replaceAprovacoes] = useLumiEntityState<Aprovacao>('aprovacoes', SEED_APROVACOES)
  const [alocacoesColaboradores, setAlocacoesColaboradores, replaceAlocacoesColaboradores] = useLumiEntityState<AlocacaoColaborador>('alocacoes_colaboradores', SEED_ALOCACOES)
  const [logs, setLogs, replaceLogs] = useLumiEntityState<LogAuditoria>('logs_auditoria', SEED_LOGS)

  useEffect(() => {
    function handleAppNotification(event: Event) {
      const detail = (event as CustomEvent<{ message: string; type?: 'success' | 'error' }>).detail
      setAppNotification({ message: detail.message, type: detail.type ?? 'error' })
      window.setTimeout(() => setAppNotification(null), 4500)
    }

    window.addEventListener('app-notification', handleAppNotification)
    return () => window.removeEventListener('app-notification', handleAppNotification)
  }, [])

  useEffect(() => {
    const authTerms = ['auth', 'login', 'sess', 'token', 'user', 'usuario']
    const dataTerms = [
      'cliente', 'contrato', 'unidade', 'obra', 'centro', 'fornecedor',
      'categoria', 'cargo', 'faixa', 'colaborador', 'orcado', 'requisicao',
      'cotacao', 'ordem', 'conta', 'pagamento', 'aprovacao', 'auditoria',
      'alocacao', 'cronograma', 'rdo', 'medicao', 'nota', 'documento',
      'publicacao', 'app4s', '4s',
    ]

    function clearLegacyStorage(storage: Storage) {
      Object.keys(storage).forEach(key => {
        const normalizedKey = key.toLowerCase()
        const looksLikeData = dataTerms.some(term => normalizedKey.includes(term))
        const looksLikeAuth = authTerms.some(term => normalizedKey.includes(term))

        if (looksLikeData && !looksLikeAuth) {
          storage.removeItem(key)
        }
      })
    }

    clearLegacyStorage(localStorage)
    clearLegacyStorage(sessionStorage)
  }, [])

  useEffect(() => {
    let active = true

    async function loadEntity<T extends PersistableRecord>(
      entityName: string,
      seed: T[],
      replaceItems: (records: T[]) => void,
    ) {
      try {
        const { list } = await withLumiRetry(() => getEntityClient<T>(entityName).list({ sort: { updatedAt: -1 } }))
        if (!active) return

        const activeRecords = compactEntityVersions(list)

        if (activeRecords.length > 0) {
          replaceItems(activeRecords)
          return
        }

        const seededRecords = await createSeedRecords(entityName, seed)
        if (!active) return

        replaceItems(seededRecords.length > 0 ? seededRecords.map(normalizeEntityRecord) : seed)
      } catch (error) {
        console.warn(`Falha ao carregar ${entityName} do Lumi:`, error)
      }
    }

    async function loadEntities() {
      const jobs = [
        () => loadEntity('clientes', SEED_CLIENTES, replaceClientes),
        () => loadEntity('unidades', SEED_UNIDADES, replaceUnidades),
        () => loadEntity('contratos', SEED_CONTRATOS, replaceContratos),
        () => loadEntity('obras', SEED_OBRAS, replaceObras),
        () => loadEntity('centros_de_custo', SEED_CENTROS, replaceCentrosCusto),
        () => loadEntity('fornecedores', SEED_FORNECEDORES, replaceFornecedores),
        () => loadEntity('categorias_orcamentarias', SEED_CATEGORIAS, replaceCategorias),
        () => loadEntity('cargos_funcoes', [], replaceCargos),
        () => loadEntity('faixas_custo', [], replaceFaixasCusto),
        () => loadEntity('colaboradores', [], replaceColaboradores),
        () => loadEntity('itens_orcados', SEED_ITENS, replaceItensOrcados),
        () => loadEntity('requisicoes', SEED_REQUISICOES, replaceRequisicoes),
        () => loadEntity('itens_requisicao', SEED_ITENS_REQUISICAO, replaceItensRequisicao),
        () => loadEntity('cotacoes', SEED_COTACOES, replaceCotacoes),
        () => loadEntity('itens_cotacao', SEED_ITENS_COTACAO, replaceItensCotacao),
        () => loadEntity('ordens_compra', SEED_ORDENS, replaceOrdensCompra),
        () => loadEntity('itens_ordem_compra', SEED_ITENS_OC, replaceItensOrdemCompra),
        () => loadEntity('contas_pagar', SEED_CONTAS, replaceContasPagar),
        () => loadEntity('pagamentos', SEED_PAGAMENTOS, replacePagamentos),
        () => loadEntity('aprovacoes', SEED_APROVACOES, replaceAprovacoes),
        () => loadEntity('alocacoes_colaboradores', SEED_ALOCACOES, replaceAlocacoesColaboradores),
        () => loadEntity('logs_auditoria', SEED_LOGS, replaceLogs),
      ]

      for (const job of jobs) {
        if (!active) return
        await job()
      }
    }

    void loadEntities()

    return () => {
      active = false
    }
  }, [
    replaceAlocacoesColaboradores,
    replaceAprovacoes,
    replaceCargos,
    replaceCategorias,
    replaceCentrosCusto,
    replaceClientes,
    replaceColaboradores,
    replaceContasPagar,
    replaceContratos,
    replaceCotacoes,
    replaceFaixasCusto,
    replaceFornecedores,
    replaceItensCotacao,
    replaceItensOrcados,
    replaceItensOrdemCompra,
    replaceItensRequisicao,
    replaceLogs,
    replaceObras,
    replaceOrdensCompra,
    replacePagamentos,
    replaceRequisicoes,
    replaceUnidades,
  ])

  useEffect(() => {
    let active = true

    async function loadUsers() {
      const storedSession = initialAuthSessionRef.current

      try {
        const initialUsers = await readUsersFromLumi()

        if (!active) return

        await syncRequiredUsersInLumi(initialUsers)
        const refreshedUsers = await readUsersFromLumi()

        if (!active) return

        setUsers(refreshedUsers)
        setUsersPersistence('lumi')

        if (storedSession) {
          const restoredUser = refreshedUsers.find(user =>
            user.status === 'ATIVO'
            && (
              user.id === storedSession.userId
              || normalizeEmail(user.email) === storedSession.email
            ),
          )

          if (restoredUser) {
            setCurrentUser(restoredUser)
          } else {
            clearStoredAuthSession()
          }
        }
      } catch (error) {
        console.warn('Persistencia compartilhada de usuarios indisponivel:', error)
        if (active) {
          setUsers(SEED_USERS)
          setUsersPersistence('memory')

          if (storedSession) {
            const restoredUser = SEED_USERS.find(user =>
              user.status === 'ATIVO'
              && (
                user.id === storedSession.userId
                || normalizeEmail(user.email) === storedSession.email
              ),
            )

            if (restoredUser) {
              setCurrentUser(restoredUser)
            } else {
              clearStoredAuthSession()
            }
          }
        }
      } finally {
        if (active) {
          setUsersLoading(false)
          setAuthSession(current => ({ ...current, checked: true }))
        }
      }
    }

    loadUsers()

    return () => {
      active = false
    }
  }, [])

  const login = useCallback((email: string, senha: string): boolean => {
    const normalizedEmailValue = normalizeEmail(email)
    const found = users.find(u => normalizeEmail(u.email) === normalizedEmailValue)
    if (found && found.status === 'ATIVO' && checkPassword(found, senha)) {
      setCurrentUser(found)
      writeStoredAuthSession(found)
      setAuthSession({ session: { userId: found.id, email: normalizeEmail(found.email) }, checked: true })
      return true
    }
    return false
  }, [users])

  const logout = useCallback(() => {
    clearStoredAuthSession()
    setAuthSession({ session: null, checked: true })
    setCurrentUser(null)
  }, [])

  const saveUser = useCallback(async (input: Omit<AppUser, 'id'> & { id?: string; senha?: string }, userId?: string) => {
    const now = nowISO()
    const localExisting = userId ? users.find(user => user.id === userId) : null
    const lumiUsers = await readUsersFromLumi()
    const existing = localExisting
      ? lumiUsers.find(user =>
          user.id === localExisting.id
          || normalizeEmail(user.email) === normalizeEmail(localExisting.email),
        ) ?? localExisting
      : null
    const senhaInformada = input.senha?.trim()
    const senhaHash = senhaInformada
      ? hashPassword(senhaInformada)
      : existing?.senha_hash ?? (existing ? hashPassword('admin123') : undefined)
    const senhaAlteradaEm = senhaInformada ? now : existing?.senha_alterada_em ?? (existing ? now : undefined)

    if (!existing) {
      const passwordError = validatePassword(senhaInformada ?? '')
      if (passwordError) throw new Error(passwordError)
    }

    if (senhaInformada) {
      const passwordError = validatePassword(senhaInformada)
      if (passwordError) throw new Error(passwordError)
    }

    const nextUser: AppUser = {
      ...(existing ?? {}),
      id: userId ?? input.id ?? `u${Date.now()}`,
      nome: input.nome,
      email: normalizeEmail(input.email),
      perfil: input.perfil,
      status: input.status,
      senha_hash: senhaHash,
      senha_alterada_em: senhaAlteradaEm,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    }

    try {
      await withLumiRetry(() => usuariosEntity.create({
        ...userPayload(nextUser),
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      }))
      const refreshedUsers = await readUsersFromLumi()
      const saved = refreshedUsers.find(user => user.id === nextUser.id || normalizeEmail(user.email) === normalizeEmail(nextUser.email))
      if (!saved) {
        throw new Error('Erro, tente novamente mais tarde.')
      }
      setUsers(refreshedUsers)
      setUsersPersistence('lumi')
      return saved
    } catch (error) {
      console.error('Falha ao salvar usuario no Lumi:', error)
      throw error
    }
  }, [users])

  const setUserStatus = useCallback(async (userId: string, status: AppUser['status']) => {
    const user = users.find(item => item.id === userId)
    if (!user) return
    await saveUser({ ...user, status }, user.id)
  }, [saveUser, users])

  const deleteUser = useCallback(async (userId: string) => {
    const localUser = users.find(item => item.id === userId)
    const lumiUsers = await readUsersFromLumi()
    const user = localUser
      ? lumiUsers.find(item =>
          item.id === localUser.id
          || normalizeEmail(item.email) === normalizeEmail(localUser.email),
        )
      : null
    if (!user) return

    try {
      const now = nowISO()
      await withLumiRetry(() => usuariosEntity.create({
        ...userPayload({
          ...user,
          deletedAt: now,
          updatedAt: now,
        }),
        createdAt: user.createdAt ?? now,
        updatedAt: now,
        deletedAt: now,
      }))
      const refreshedUsers = await readUsersFromLumi()
      const stillExists = refreshedUsers.some(item => item.id === userId || normalizeEmail(item.email) === normalizeEmail(user.email))
      if (stillExists) {
        throw new Error('Erro, tente novamente mais tarde.')
      }
      setUsers(refreshedUsers)
      setUsersPersistence('lumi')

      if (currentUser?.id === userId) {
        clearStoredAuthSession()
        setAuthSession({ session: null, checked: true })
        setCurrentUser(null)
      }
    } catch (error) {
      console.error('Falha ao excluir usuario no Lumi:', error)
      throw error
    }
  }, [currentUser, users])

  const changeOwnPassword = useCallback(async (senhaAtual: string, novaSenha: string) => {
    if (!currentUser) throw new Error('Usuario nao autenticado.')

    const user = users.find(item => item.id === currentUser.id)
    if (!user) throw new Error('Usuario nao encontrado.')

    if (!checkPassword(user, senhaAtual)) {
      throw new Error('Senha atual incorreta.')
    }

    const passwordError = validatePassword(novaSenha)
    if (passwordError) throw new Error(passwordError)

    const saved = await saveUser({ ...user, senha: novaSenha }, user.id)
    setCurrentUser(saved)
  }, [currentUser, saveUser, users])

  const addLog = useCallback((entidade_tipo: string, entidade_id: string, acao: LogAuditoria['acao'], detalhes: string) => {
    setLogs(prev => [{
      id: createAuditLogId(),
      entidade_tipo,
      entidade_id,
      acao,
      usuario_nome: currentUser?.nome || 'Sistema',
      detalhes,
      createdAt: nowISO(),
    }, ...prev])
  }, [currentUser, setLogs])

  return (
    <AppContext.Provider value={{
      currentUser, users, clientes, unidades, contratos, obras, centrosCusto,
      fornecedores, categorias, cargos, faixasCusto, colaboradores, itensOrcados,
      requisicoes, itensRequisicao, cotacoes, itensCotacao, ordensCompra, itensOrdemCompra,
      contasPagar, pagamentos, aprovacoes, alocacoesColaboradores, logs,
      usersLoading, usersPersistence,
      authSessionRestoring: Boolean(authSession.session) && !authSession.checked,
      login, logout, saveUser, setUserStatus, deleteUser, changeOwnPassword,
      setClientes, setUnidades, setContratos, setObras, setCentrosCusto,
      setFornecedores, setCategorias, setCargos, setFaixasCusto, setColaboradores,
      setItensOrcados, setRequisicoes, setItensRequisicao, setCotacoes, setItensCotacao,
      setOrdensCompra, setItensOrdemCompra, setContasPagar, setPagamentos,
      setAprovacoes, setAlocacoesColaboradores, addLog,
    }}>
      {children}
      {appNotification && (
        <div
          style={{
            position: 'fixed',
            right: 24,
            top: 20,
            zIndex: 9999,
            maxWidth: 420,
            background: appNotification.type === 'error' ? '#2a1518' : '#102416',
            border: `1px solid ${appNotification.type === 'error' ? 'rgba(248,113,113,0.55)' : 'rgba(52,211,153,0.55)'}`,
            color: appNotification.type === 'error' ? '#fecaca' : '#bbf7d0',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 13,
            boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
          }}
        >
          {appNotification.message}
        </div>
      )}
    </AppContext.Provider>
  )
}

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp fora do AppProvider')
  return ctx
}
